import {CdkPortalOutlet} from "@angular/cdk/portal";
import {
    Component,
    ComponentRef,
    forwardRef,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ViewChild
} from "@angular/core";
import {NG_VALUE_ACCESSOR} from "@angular/forms";
import {Subscription} from "rxjs";
import {FormControlBaseComponent} from "../../../../shared/components/form-controls/form-control-base/form-control-base.component";
import {ProductCollectionsEnum} from "../../../../shared/enums/product-collections.enum";
import {EmptySearchParamsFilterModel} from "../../../../shared/models/search/params/filters/empty-search-params-filter.model";
import {ProductResultItemModel} from "../../../../shared/models/search/results/items/product-result-item.model";
import {SearchResultModel} from "../../../../shared/models/search/results/search-result.model";
import {CollectionFilterBaseForm} from "../../../forms/collection-filter-base.form";
import {CollectionResultsFilter} from "../../../models/search-results/filters/collection-results-filter";
import {CollectionFilterFactoryService} from "../../../services/factories/collection-filter-factory.service";
import {CollectionFilterDirective} from "../../filters/collection-filter.directive";

@Component({
    selector: "pcg-ng-collection-filter-proxy",
    template: require('./collection-filter-proxy.component.html'),
    providers: [
        {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CollectionFilterProxyComponent), multi: true}
    ]
})
export class CollectionFilterProxyComponent extends FormControlBaseComponent<EmptySearchParamsFilterModel> implements OnChanges, OnDestroy {
    @Input()
    public collection: ProductCollectionsEnum;

    @Input()
    public searchResult: SearchResultModel<ProductResultItemModel, CollectionResultsFilter>;

    @ViewChild(CdkPortalOutlet)
    public outlet: CdkPortalOutlet;

    public collectionFilter: ComponentRef<CollectionFilterDirective<CollectionFilterBaseForm<EmptySearchParamsFilterModel>, EmptySearchParamsFilterModel>>;

    private changeSubscription: Subscription;

    public constructor(
        private collectionFilterFactory: CollectionFilterFactoryService
    ) {
        super(/*DEFAULT_VALUE*/{});
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const {searchResult} = changes;
        if (searchResult && searchResult.currentValue !== searchResult.previousValue) {
            this.setSearchResult(searchResult.currentValue);
        }
    }

    public clearFilter(): void {
        this.collectionFilter.instance.clearForm();
    }

    public ngOnDestroy(): void {
        this.unSubscribeChanges();
        if (this.outlet) {
            this.outlet.dispose();
        }
    }

    public createCollectionFilter(): void  {
        this.cleanUp();
        this.mountCollectionFilter();
        this.initialiseCollectionFilter();
    }

    private mountCollectionFilter(): void  {
        const portal = this.collectionFilterFactory.createCollectionFilterPortal(this.collection);
        this.collectionFilter = this.outlet.attachComponentPortal(portal);
    }

    private initialiseCollectionFilter(): void  {
        this.setSearchResult(this.searchResult);
        this.setSearchParamsFilter(this.controlValue);

        this.changeSubscription = this.collectionFilter.instance.filterSubmitted
            .subscribe((filter: EmptySearchParamsFilterModel) => {
                this.writeNewValue(filter);
            });
    }

    private setSearchParamsFilter(value: EmptySearchParamsFilterModel): void  {
        if (!this.collectionFilter) {
            return;
        }

        this.collectionFilter.instance.filter = value;
    }

    private setSearchResult(searchResult: SearchResultModel<ProductResultItemModel, CollectionResultsFilter>): void  {
        if (!this.collectionFilter) {
            return;
        }
        this.collectionFilter.instance.searchResult = searchResult;
    }

    private unSubscribeChanges(): void  {
        if (this.changeSubscription) {
            this.changeSubscription.unsubscribe();
            this.changeSubscription = null;
        }
    }

    private cleanUp(): void  {
        this.unSubscribeChanges();
        if (!this.outlet || !this.outlet.hasAttached()) {
            return;
        }

        this.outlet.detach();
        this.collectionFilter = null;
    }
}
